/**
 * Az időjárás-widget HELYE és IDEJE — a komponenstől külön, tesztelhetően.
 *
 * Geri kérése (GRUNDO #61): a widget alatt jobbra igazítva egy kis sor —
 * „Budapest, 15:42 ⓘ" —, rákoppintva kereshető városlista, és a választott
 * város időjárása ÉS pontos ideje látszik. Frissítés: appindításkor, lehúzásra
 * csak ha 15 percnél régebbi az adat, egyébként magától 15 percenként.
 */

import { isNativeApp } from '@/lib/platform';
import { GOOD_ENOUGH_M, SHARED_MAX_AGE_MS, USELESS_ABOVE_M } from '@/lib/positionQuality';
import { nativeLocationDiagnostics } from '@/tracking/nativeSource';

/** Ennyi idő után régi az időjárás — Geri: „15 percenként". */
export const WEATHER_REFRESH_MS = 15 * 60 * 1000;

/**
 * A választott város. `null` = a saját helyzetem (ez az alapértelmezés).
 *
 * A `users/{uid}/private/settings.weatherPlace` mezőben él — ugyanott, ahol
 * az értesítés-kapcsolók: a tulajdonos maga írja, nem játékadat.
 */
export interface WeatherPlaceChoice {
  name: string;
  lat: number;
  lon: number;
}

/** A tárolt mezőből a választás; minden hibás alak `null` (= saját helyzet). */
export function readPlaceChoice(raw: unknown): WeatherPlaceChoice | null {
  if (!raw || typeof raw !== 'object') return null;
  const value = raw as Record<string, unknown>;
  const name = typeof value.name === 'string' ? value.name.trim() : '';
  const lat = Number(value.lat);
  const lon = Number(value.lon);
  if (!name || !Number.isFinite(lat) || !Number.isFinite(lon)) return null;
  if (Math.abs(lat) > 90 || Math.abs(lon) > 180) return null;
  return { name, lat, lon };
}

/** A választás kulcsa — a gyorsítótár ettől tudja, ugyanarról a helyről szól-e. */
export function placeKey(choice: WeatherPlaceChoice | null): string {
  return choice ? `${choice.lat.toFixed(2)},${choice.lon.toFixed(2)}` : 'here';
}

/**
 * „15:42" — a HELY idejében, nem a készülékében.
 *
 * Ismeretlen vagy hiányzó zónánál a készülék ideje: a sor így sem marad üres,
 * és a saját helyzetnél ez úgyis ugyanaz.
 */
export function formatPlaceTime(now: Date, timeZone: string | null | undefined): string {
  const options: Intl.DateTimeFormatOptions = {
    hour: '2-digit',
    minute: '2-digit',
    ...(getLocale() === 'hu' ? { hourCycle: 'h23' as const } : {}),
  };
  try {
    return new Intl.DateTimeFormat(getDateTag(), timeZone ? { ...options, timeZone } : options).format(now);
  } catch {
    return new Intl.DateTimeFormat(getDateTag(), options).format(now);
  }
}

/**
 * Van-e MÁR megadott helyengedély — kérdés NÉLKÜL kiderítve.
 *
 * ⚠️ Ez a lényeg: a widget csak akkor kér friss helyzetet magától, ha ez
 * `true`. Engedélykérő ablak egy időjárás-csempéért az indításkor a
 * legbiztosabb módja, hogy a felhasználó a térképtől is megtagadja a helyet
 * (lásd `WeatherWidget` fejléce).
 *
 * Natívban a helyzet-plugin diagnosztikája mondja meg (a WebView
 * `navigator.permissions`-e ott semmit nem tud); böngészőben a Permissions
 * API. Bármi hiba: `false` — ilyenkor marad a tárolt pozíció.
 */
export async function hasLocationPermission(): Promise<boolean> {
  try {
    if (isNativeApp()) {
      const { authorization } = await nativeLocationDiagnostics();
      return authorization === 'always' || authorization === 'whenInUse';
    }
    if (typeof navigator === 'undefined' || !navigator.permissions?.query) return false;
    const status = await navigator.permissions.query({ name: 'geolocation' });
    return status.state === 'granted';
  } catch {
    return false;
  }
}

/** Egy helyzet a döntéshez: hol, milyen pontos, mikor mérték. */
export interface PositionSample {
  lat: number;
  lon: number;
  accuracyM: number;
  /** Mérés ideje (epoch ms). */
  at: number;
}

/**
 * A friss mérés és a tárolt (megosztott) helyzet közül melyik szóljon.
 *
 * ⚠️ MÉRT HIBA (GRUNDO #61): asztali gépen a friss mérés IP-alapú becslés
 * volt, és az időjárás „Mándok"-ot írt Budapest XI. helyett. Ugyanaz a
 * szabály, mint a térképnél (`useSharedPosition`):
 *
 *   1. a pontos friss mérés (≤ 150 m) nyer;
 *   2. különben a tárolt, ha 24 órán belüli és pontosabb a frissnél;
 *   3. különben a friss, ha nem teljesen használhatatlan (≤ 3 km);
 *   4. végül ami van — egy rossz hely is jobb a semminél, a városnév-sor
 *      úgyis megmutatja, és onnan várost lehet választani.
 */
export function pickWeatherPosition(
  fresh: PositionSample | null,
  stored: PositionSample | null,
  now: number,
): PositionSample | null {
  if (fresh && fresh.accuracyM <= GOOD_ENOUGH_M) return fresh;
  const storedUsable = stored !== null && now - stored.at <= SHARED_MAX_AGE_MS;
  if (storedUsable && (!fresh || stored.accuracyM < fresh.accuracyM)) return stored;
  if (fresh && fresh.accuracyM <= USELESS_ABOVE_M) return fresh;
  return stored ?? fresh;
}
import { getDateTag, getLocale } from '@/i18n';
